import type { Website, WebsiteEventGoal } from 'types';
import type { EVENT_GOAL_ROLES } from 'definitions';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';

export type EventGoalRole = (typeof EVENT_GOAL_ROLES)[number];

type WebsiteSettings = {
  defaultCurrency: string;
  trackingMethod: string | null;
  conversionEvent: string | null;
};

export const useWebsiteDetails = (websiteId: number) => {
  const apiClient = useClient();
  const queryClient = useQueryClient();

  const websiteQuery = useQuery<Website>({
    queryKey: ['WEBSITE', websiteId],
    queryFn: () => apiClient.get(`/websites/${websiteId}`),
    enabled: !!websiteId,
  });

  const eventGoalsQuery = useQuery<WebsiteEventGoal[]>({
    queryKey: ['WEBSITE_EVENT_GOALS', websiteId],
    queryFn: () => apiClient.get(`/websites/${websiteId}/event-goals`),
    enabled: !!websiteId,
  });

  const updateEventGoalRole = async (goalId: number, role: EventGoalRole) => {
    await apiClient.put(`/websites/${websiteId}/event-goals/${goalId}`, { role });
    queryClient.invalidateQueries({ queryKey: ['WEBSITE_EVENT_GOALS', websiteId] });
  };

  const updateSettings = async (settings: WebsiteSettings) => {
    await apiClient.put(`/websites/${websiteId}/settings`, settings);
    queryClient.invalidateQueries({ queryKey: ['WEBSITE', websiteId] });
  };

  return {
    website: websiteQuery.data,
    eventGoals: eventGoalsQuery.data,
    isLoading: websiteQuery.isLoading || eventGoalsQuery.isLoading,
    updateEventGoalRole,
    updateSettings,
  };
};
